import { INVALID_EMAIL_ERROR_MESSAGE } from '@/shared/config';
import { useSetState } from '@/shared/lib/customHooks';
import { validateEmail } from '@/shared/lib';
import _ from 'lodash';
import { useEffect } from 'react';
import { t } from 'ttag';
import type { ModalEditEmailProps } from './modalEditEmail.types';

/**
 * Manages the modal for editing a support request's notification email list.
 * Holds the edited list, pending input and errors, resets them from data each time the modal opens,
 * and validates before saving through handleUpdateEmail.
 *
 * @param props - Props carrying isOpen, onClose, data and handleUpdateEmail.
 * @returns An object carrying state, handleClickSave, onChangeEmailList and onInputChange.
 */
export const useModalEditEmail = ({
  isOpen,
  onClose,
  data,
  handleUpdateEmail,
}: ModalEditEmailProps) => {
  const [state, setState] = useSetState<any>({
    isSaving: false,
    emailList: data,
    errorObject: {},
    emailInputValue: '',
  });

  /**
   * Validates and saves the notification email list of a support request. Rejects an unfinished input failing validateEmail and a list equal to the original, in both cases writing an invalidEmails message into errorObject and dropping isSaving, otherwise sends the comma-joined list through handleUpdateEmail before clearing the flag and closing the modal.
   *
   * @returns Promise resolving once the emails were saved or a validation error was shown.
   */
  const handleClickSave = async () => {
    setState({ isSaving: true });
    const errorObject = {};
    const isValidCurrentInput = !state.emailInputValue || validateEmail(state.emailInputValue);
    if (!isValidCurrentInput) {
      _.assign(errorObject, { invalidEmails: INVALID_EMAIL_ERROR_MESSAGE });
      setState({
        errorObject,
        isSaving: false,
      });
      return;
    }

    if (_.isEqual(data, state.emailList)) {
      _.assign(errorObject, { invalidEmails: t`You did not make any changes` });
      setState({
        errorObject,
        isSaving: false,
      });
      return;
    }

    await handleUpdateEmail(_.join(state.emailList, ','));
    setState({ isSaving: false });
    onClose();
  };

  /**
   * Replaces the notification email list of a support request with the addresses entered as chips. Stores the new list while clearing the pending text input and any validation errors.
   *
   * @param value - New list of email addresses.
   * @returns Nothing.
   */
  const onChangeEmailList = (value: any) => {
    setState({
      emailList: value,
      emailInputValue: '',
      errorObject: {},
    });
  };

  /**
   * Tracks the email address currently being typed in the support request email editor. Stores the raw text and clears previous validation errors.
   *
   * @param value - Current text of the email input.
   * @returns Nothing.
   */
  const onInputChange = (value: any) => {
    setState({
      emailInputValue: value,
      errorObject: {},
    });
  };

  useEffect(() => {
    if (isOpen) {
      setState({
        emailList: data,
        emailInputValue: '',
        errorObject: {},
      });
    }
  }, [isOpen, data]);

  return {
    state,
    handleClickSave,
    onChangeEmailList,
    onInputChange,
  };
};
